import { useState, useEffect, useRef } from 'react';
import { useWakeLock } from '@/hooks/useWakeLock';
import { useVoiceGuidance } from '@/hooks/useVoiceGuidance';
import { X, Play, Pause, RotateCcw, Volume2, VolumeX, Home } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { StepperInput } from './StepperInput';
import { Label } from '@/components/ui/label';
import { playMetronomeTick } from '@/utils/audioUtils';
import BreathingBar from './BreathingBar';
import { EXERCISE_BACKGROUNDS, EXERCISE_BG_FALLBACK_COLOR } from '@/utils/exerciseBackgrounds';
import ExerciseCopyright from './ExerciseCopyright';
import ExerciseInstructionsDrawer from './ExerciseInstructionsDrawer';
import ExerciseTopLogo from './ExerciseTopLogo';

const CalmingBreathModal = ({ isOpen, onClose, pattern, onSessionComplete }) => {
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [phase, setPhase] = useState('ready'); // ready, inhale, exhale
  useWakeLock(isActive && !isPaused);
  useVoiceGuidance(phase, isActive && !isPaused);
  const [progress, setProgress] = useState(0);
  const [cyclesCompleted, setCyclesCompleted] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [sessionDuration, setSessionDuration] = useState(5); // minutes
  const [remainingTime, setRemainingTime] = useState(300); // seconds
  const [inhaleDuration, setInhaleDuration] = useState(3);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  const intervalRef = useRef(null);
  const timerRef = useRef(null);
  const soundEnabledRef = useRef(true);

  // Keep ref in sync with state
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  const exhaleDuration = inhaleDuration * 2; // Always 2:1 ratio

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsActive(false);
      setIsPaused(false);
      setPhase('ready');
      setProgress(0);
      setCyclesCompleted(0);
      setRemainingTime(sessionDuration * 60);
    }
  }, [isOpen, sessionDuration]);

  // Update remaining time when session duration changes
  useEffect(() => {
    if (!isActive) {
      setRemainingTime(sessionDuration * 60);
    }
  }, [sessionDuration, isActive]);

  // Main breathing logic
  useEffect(() => {
    if (!isActive || isPaused) {
      if (intervalRef.current) {
        if (typeof intervalRef.current === 'number') cancelAnimationFrame(intervalRef.current);
      }
      return;
    }

    let phaseStartTime = Date.now();
    let currentPhase = phase === 'ready' ? 'inhale' : phase;
    if (phase === 'ready') setPhase('inhale');

    const getPhaseDuration = (p) => {
      if (p === 'inhale') return inhaleDuration * 1000;
      if (p === 'exhale') return exhaleDuration * 1000;
      return 1000;
    };

    // requestAnimationFrame drives progress at ~60 FPS so the capsule fill
    // feels perfectly smooth, instead of the 20 FPS step motion a 50 ms
    // setInterval produces (which combined with the previous 100 ms CSS
    // transition caused the saccades the patient could see).
    let rafId = null;
    const tick = () => {
      const elapsed = Date.now() - phaseStartTime;
      const phaseDuration = getPhaseDuration(currentPhase);
      const prog = Math.min(elapsed / phaseDuration, 1);
      setProgress(prog);

      if (elapsed >= phaseDuration) {
        if (currentPhase === 'inhale') {
          currentPhase = 'exhale';
          setPhase('exhale');
        } else if (currentPhase === 'exhale') {
          currentPhase = 'inhale';
          setPhase('inhale');
          setCyclesCompleted(prev => prev + 1);
        }
        phaseStartTime = Date.now();
        setProgress(0);
      }

      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    intervalRef.current = rafId;

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isActive, isPaused, phase, inhaleDuration, exhaleDuration]);

  // Metronome - tick every second
  useEffect(() => {
    if (!isActive || isPaused) return;
    
    if (soundEnabledRef.current) playMetronomeTick();
    
    const metronomeInterval = setInterval(() => {
      if (soundEnabledRef.current) playMetronomeTick();
    }, 1000);
    
    return () => clearInterval(metronomeInterval);
  }, [isActive, isPaused]);

  // Session timer
  useEffect(() => {
    if (!isActive || isPaused) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
      setRemainingTime(prev => {
        if (prev <= 1) {
          if (onSessionComplete && elapsedTime > 10) {
            onSessionComplete({
              patternId: pattern?.id || 'calming-breath',
              patternName: pattern?.name || 'Respiration Calmante',
              durationSeconds: elapsedTime,
              cyclesCompleted: cyclesCompleted
            });
          }
          setIsActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isActive, isPaused, elapsedTime, cyclesCompleted, onSessionComplete, pattern]);

  const handleStart = () => {
    setElapsedTime(0);
    setIsActive(true);
    setIsPaused(false);
    setPhase('inhale');
    setProgress(0);
  };

  const handlePause = () => setIsPaused(!isPaused);

  const handleReset = () => {
    if (onSessionComplete && elapsedTime > 10) {
      onSessionComplete({
        patternId: pattern?.id || 'calming-breath',
        patternName: pattern?.name || 'Respiration Calmante',
        durationSeconds: elapsedTime,
        cyclesCompleted: cyclesCompleted
      });
    }
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setProgress(0);
    setCyclesCompleted(0);
    setRemainingTime(sessionDuration * 60);
    setElapsedTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPhaseLabel = () => {
    if (phase === 'ready') return 'Prêt';
    if (phase === 'inhale') return 'Inspirez';
    if (phase === 'exhale') return 'Expirez';
    return '';
  };

  const getPhaseColor = () => {
    if (phase === 'inhale') return 'from-amber-300 to-orange-400';
    if (phase === 'exhale') return 'from-orange-400 to-amber-500';
    return 'from-amber-200 to-amber-300';
  };

  const getCurrentPhaseDuration = () => {
    if (phase === 'inhale') return inhaleDuration;
    if (phase === 'exhale') return exhaleDuration;
    return 0;
  };

  // Calculate circle scale based on phase
  const getCircleScale = () => {
    if (phase === 'inhale') return 1 + progress * 0.3;
    if (phase === 'exhale') return 1.3 - progress * 0.3;
    return 1;
  };

  // Wave animation
  const getWaveOffset = () => {
    if (phase === 'inhale') return 100 - progress * 100;
    if (phase === 'exhale') return progress * 100;
    return 100;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <ExerciseCopyright />
      <div 
        className="relative w-full h-full flex flex-col overflow-hidden"
        style={{
          backgroundImage: `url(${EXERCISE_BACKGROUNDS.calming})`,
          backgroundColor: EXERCISE_BG_FALLBACK_COLOR,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-black/30" />

        {/* Floating Navigation Buttons */}
        <div className="absolute top-4 left-4 z-20">
          <button
            onClick={onClose}
            className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
            title="Retour aux exercices"
          >
            <Home size={20} className="text-white" />
          </button>
        </div>
        
        <div className="absolute top-4 right-4 z-20">
          <button
            onClick={onClose}
            className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
            title="Fermer"
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        <ExerciseTopLogo />

        {/* Main Content */}
        <div className="relative flex-1 overflow-y-auto px-4 pt-20 sm:pt-24 pb-40 flex flex-col lg:items-center lg:justify-center lg:pb-8">
          <div className="flex flex-col items-center my-auto gap-6 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-6 lg:items-center w-full">
            
            {/* Settings Panel - Left - Show when not active OR when paused */}
            <div className="w-full max-w-xs order-3 lg:order-none lg:col-start-1 lg:row-start-1 lg:justify-self-end lg:w-64">
              {/* Timer as a stats card (moved from the top-center overlay so the
                  header stays free for the mobile brand logo). */}
              <div className="bg-white/85 backdrop-blur-md rounded-xl px-3 py-3 text-center border border-slate-300 shadow-md mb-4">
                <p className="text-slate-500 text-xs mb-1">Temps restant</p>
                <p className="text-slate-800 font-bold text-2xl" style={{ fontFamily: 'system-ui' }}>
                  {formatTime(remainingTime)}
                </p>
              </div>
              {(!isActive || isPaused) && (
                <div className="bg-white/90 backdrop-blur rounded-2xl p-3 shadow-xl">
                  <h3 className="font-semibold text-slate-700 text-sm mb-2">
                    {isPaused ? 'Pause - Réglages' : 'Réglages'}
                  </h3>
                  
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-600 mb-1 block">Durée</Label>
                        <StepperInput
                          value={sessionDuration}
                          onChange={(v) => {
                            setSessionDuration(v);
                            if (isPaused) setRemainingTime(v * 60);
                          }}
                          min={1}
                          max={20}
                          step={1}
                          unit="min"
                          label="durée"
                          colorClass="emerald"
                          valueTestId="calming-duration-value"
                          incTestId="calming-duration-inc"
                          decTestId="calming-duration-dec"
                        />
                      </div>

                      <div>
                        <Label className="text-xs text-slate-600 mb-1 block">Inspiration</Label>
                        <StepperInput
                          value={inhaleDuration}
                          onChange={(v) => setInhaleDuration(v)}
                          min={2}
                          max={5}
                          step={1}
                          unit="sec"
                          label="inspiration"
                          colorClass="sky"
                          valueTestId="calming-inhale-value"
                          incTestId="calming-inhale-inc"
                          decTestId="calming-inhale-dec"
                        />
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-500 pt-1 border-t border-slate-200">
                      Expir auto {exhaleDuration}s · {Math.round(60 / (inhaleDuration + exhaleDuration))} resp/min
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Center - Wave + Controls (controls hidden on desktop, moved to right column) */}
            <div className="flex flex-col items-center order-1 lg:order-none lg:col-start-2 lg:row-start-1">

              {/* Breathing Bar */}
              <div className="w-full flex justify-center">
                <BreathingBar
                  phase={phase}
                  progress={progress}
                  phases={['inhale', 'exhale']}
                  phaseDurations={{
                    inhale: inhaleDuration,
                    exhale: exhaleDuration
                  }}
                  colors={{
                    inhale: '#fbbf24', // amber
                    exhale: '#f97316', // orange
                    ready: '#94a3b8'
                  }}
                />
              </div>

              {/* Phase indicator - Now integrated in BreathingBar */}
              {isActive && (
                <div className="mt-2 bg-white/80 backdrop-blur rounded-xl px-6 py-2 shadow-lg">
                  <span className="text-lg font-medium text-slate-600">{getPhaseLabel()}</span>
                </div>
              )}

              {/* Controls (mobile only — desktop version lives in right column) */}
              <div className="flex items-center gap-4 mt-6 exercise-controls-mobile-fixed lg:hidden">
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className="w-14 h-14 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                >
                  {soundEnabled ? (
                    <Volume2 className="w-6 h-6 text-slate-600" />
                  ) : (
                    <VolumeX className="w-6 h-6 text-slate-400" />
                  )}
                </button>

                <button
                  onClick={isActive ? handlePause : handleStart}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                >
                  {!isActive || isPaused ? (
                    <Play className="w-8 h-8 text-slate-600 ml-1" />
                  ) : (
                    <Pause className="w-8 h-8 text-slate-600" />
                  )}
                </button>

                <button
                  onClick={handleReset}
                  className="w-14 h-14 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                >
                  <RotateCcw className="w-6 h-6 text-slate-600" />
                </button>
              </div>

              {/* Cycle counter (mobile only) */}
              <div className="mt-4 bg-white/60 backdrop-blur rounded-xl px-4 py-2 lg:hidden">
                <span className="text-slate-600 font-medium">{cyclesCompleted} cycles</span>
              </div>
            </div>

            {/* RIGHT (desktop only) — Vertical controls column */}
            <div className="hidden lg:flex lg:col-start-3 lg:row-start-1 lg:justify-self-start lg:flex-col lg:items-center gap-4">
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-14 h-14 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                data-testid="calming-sound-btn-desktop"
              >
                {soundEnabled ? (<Volume2 className="w-6 h-6 text-slate-600" />) : (<VolumeX className="w-6 h-6 text-slate-400" />)}
              </button>
              <button
                onClick={isActive ? handlePause : handleStart}
                className="w-20 h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                data-testid="calming-play-btn-desktop"
              >
                {!isActive || isPaused ? (<Play className="w-9 h-9 text-slate-600 ml-1" />) : (<Pause className="w-9 h-9 text-slate-600" />)}
              </button>
              <button
                onClick={handleReset}
                className="w-14 h-14 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                data-testid="calming-reset-btn-desktop"
              >
                <RotateCcw className="w-6 h-6 text-slate-600" />
              </button>
              <div className="bg-white/60 backdrop-blur rounded-xl px-4 py-2 mt-2">
                <span className="text-slate-600 font-medium">{cyclesCompleted} cycles</span>
              </div>
            </div>

            {/* Instructions - Right (mobile only — desktop uses drawer) */}
            <div className="w-full max-w-xs order-3 lg:hidden">
              {!isActive && (
                <div className="bg-white/90 backdrop-blur rounded-2xl p-5 shadow-xl">
                  <h3 className="font-semibold text-slate-700 mb-3">Instructions</h3>
                  <ul className="text-sm text-slate-600 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 font-bold">1.</span>
                      Asseyez-vous confortablement
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 font-bold">2.</span>
                      Inspirez doucement par le nez
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-500 font-bold">3.</span>
                      Expirez lentement (2x plus long)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-slate-500 font-bold">4.</span>
                      Laissez-vous bercer par le rythme
                    </li>
                  </ul>
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                    <p className="text-xs text-amber-700">
                      <strong>Ratio 2:1 :</strong> L&apos;expiration prolongée active le système parasympathique et induit un calme profond.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Instructions drawer (desktop only) */}
        <ExerciseInstructionsDrawer testIdPrefix="calming">
          <div className="bg-white rounded-xl p-4 shadow border border-slate-200">
            <h3 className="font-semibold text-slate-700 mb-3">Instructions</h3>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2"><span className="text-amber-500 font-bold">1.</span>Asseyez-vous confortablement</li>
              <li className="flex items-start gap-2"><span className="text-amber-500 font-bold">2.</span>Inspirez doucement par le nez</li>
              <li className="flex items-start gap-2"><span className="text-orange-500 font-bold">3.</span>Expirez lentement (2x plus long)</li>
              <li className="flex items-start gap-2"><span className="text-slate-500 font-bold">4.</span>Laissez-vous bercer par le rythme</li>
            </ul>
            <div className="mt-4 p-3 bg-amber-50 rounded-lg">
              <p className="text-xs text-amber-700">
                <strong>Ratio 2:1 :</strong> L&apos;expiration prolongée active le système parasympathique et induit un calme profond.
              </p>
            </div>
          </div>
        </ExerciseInstructionsDrawer>
      </div>
    </div>
  );
};

export default CalmingBreathModal;
